import React, { useMemo, useState } from "react";
import { Card, Button, Table } from "react-bootstrap";
import {
    ArrowLeft,
    ChevronDown,
    Download,
    Alarm,
    Lightbulb,
    Stars,
} from "react-bootstrap-icons";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { useNavigate } from "react-router-dom";

const CustomerDropOffInsightDetails = () => {
    const navigate = useNavigate();
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const [selectedRange, setSelectedRange] = useState("Last 12 Months");
    const [hoveredIndex, setHoveredIndex] = useState(null);

    const dropOffData = [
        { visit: "1st", customers: 7673 },
        { visit: "2nd", customers: 1967 },
        { visit: "3rd", customers: 1019 },
        { visit: "4th", customers: 694 },
        { visit: "5th", customers: 546 },
        { visit: "6th", customers: 447 },
        { visit: "7th", customers: 368 },
        { visit: "8th", customers: 321 },
    ];

    const targetVisit = 5;

    const processedData = useMemo(() => {
        const firstVisitCustomers = dropOffData[0]?.customers || 0;

        return dropOffData.map((item, index) => {
            const previousCustomers =
                index === 0 ? null : dropOffData[index - 1].customers;

            const retainedPercent = firstVisitCustomers
                ? (item.customers / firstVisitCustomers) * 100
                : 0;

            const dropOffCount = previousCustomers
                ? previousCustomers - item.customers
                : null;

            const dropOffPercent = previousCustomers
                ? (dropOffCount / previousCustomers) * 100
                : null;

            return {
                ...item,
                retainedPercent,
                dropOffCount,
                dropOffPercent,
            };
        });
    }, []);

    const targetData = processedData[targetVisit - 1];
    const firstVisitData = processedData[0];
    const secondVisitData = processedData[1];

    const chartData = processedData.slice(0, 5);

    const chart = useMemo(
        () => ({
            width: 760,
            height: 360,
            top: 8,
            bottom: 40,
            left: 85,
            right: 26,
            maxValue: 100,
        }),
        []
    );

    const points = useMemo(() => {
        const innerWidth = chart.width - chart.left - chart.right;
        const innerHeight = chart.height - chart.top - chart.bottom;
        const baseY = chart.top + innerHeight;

        return chartData.map((item, index) => {
            const x = chart.left + (index / (chartData.length - 1)) * innerWidth;
            const y =
                chart.top +
                innerHeight -
                (item.retainedPercent / chart.maxValue) * innerHeight;

            return {
                ...item,
                label: `${item.visit} visit`,
                value: Number(item.retainedPercent.toFixed(1)),
                x,
                y,
                baseY,
                fullBarY: chart.top,
                fullBarHeight: innerHeight,
                fillBarY: y,
                fillBarHeight: Math.max(baseY - y, 5),
            };
        });
    }, [chartData, chart]);

    const smoothPath = useMemo(() => {
        if (!points.length) return "";

        return points.reduce((path, point, index) => {
            if (index === 0) return `M ${point.x} ${point.y}`;

            const prev = points[index - 1];
            const midX = (prev.x + point.x) / 2;

            return `${path} C ${midX} ${prev.y}, ${midX} ${point.y}, ${point.x} ${point.y}`;
        }, "");
    }, [points]);

    const areaPath = useMemo(() => {
        if (!points.length || !smoothPath) return "";

        const firstPoint = points[0];
        const lastPoint = points[points.length - 1];

        return `
      ${smoothPath}
      L ${lastPoint.x} ${lastPoint.baseY}
      L ${firstPoint.x} ${firstPoint.baseY}
      Z
    `;
    }, [points, smoothPath]);

    const activePoint = hoveredIndex !== null ? points[hoveredIndex] : null;

    return (
        <>
            <Header onSkin={setSkin} />

            <div className="main main-app">
                <div style={pageWrapperStyle}>
                    <div style={topActionStyle}>
                        <Button variant="light" onClick={() => navigate('/dashboard')} style={backButtonStyle}>
                            <ArrowLeft size={18} /> Go Back
                        </Button>

                        <Button variant="light" style={exportButtonStyle}>
                            <Download size={17} /> Export
                        </Button>
                    </div>

                    <h1 style={pageTitleStyle}>
                        What percentage of our customers{" "}
                        <span style={{ color: "#dc2692" }}>
                            make it to their {targetVisit}th visit?
                        </span>
                    </h1>

                    <div style={filterBarStyle}>
                        <Button variant="light" style={filterStyle}>
                            {selectedRange} <ChevronDown size={18} />
                        </Button>
                    </div>

                    <Card style={mainCardStyle}>
                        <Card.Body style={{ padding: 28 }}>
                            <h2 style={mainMetricStyle}>
                                {targetData.retainedPercent.toFixed(0)}%{" "}
                                <span style={mutedMetricStyle}>
                                    ({targetData.customers.toLocaleString()} out of{" "}
                                    {firstVisitData.customers.toLocaleString()} Customers)
                                </span>
                            </h2>

                            <p style={subTextStyle}>
                                of all our customers make it till their {targetVisit}th visit
                            </p>

                            <div className="dropoff-grid" style={chartInsightGridStyle}>
                                <div className="details-chart-wrap">
                                    <svg
                                        viewBox={`0 0 ${chart.width} ${chart.height}`}
                                        className="details-chart-svg"
                                        preserveAspectRatio="none"
                                    >
                                        <defs>
                                            <linearGradient
                                                id="detailsCurveAreaGradient"
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="1"
                                            >
                                                <stop offset="0%" stopColor="#dc2692" stopOpacity="0.22" />
                                                <stop offset="55%" stopColor="#dc2692" stopOpacity="0.12" />
                                                <stop offset="100%" stopColor="#dc2692" stopOpacity="0.04" />
                                            </linearGradient>
                                        </defs>

                                        {[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((value) => {
                                            const y =
                                                chart.top +
                                                ((100 - value) / 100) *
                                                (chart.height - chart.top - chart.bottom);

                                            return (
                                                <g key={value}>
                                                    <text
                                                        x="12"
                                                        y={y + 4}
                                                        className="details-y-label"
                                                    >
                                                        {value}%
                                                    </text>

                                                    <line
                                                        x1={chart.left}
                                                        x2={chart.width - chart.right}
                                                        y1={y}
                                                        y2={y}
                                                        stroke="#dde4e5"
                                                        strokeWidth="1"
                                                        strokeDasharray="6 6"
                                                        vectorEffect="non-scaling-stroke"
                                                    />
                                                </g>
                                            );
                                        })}

                                        {areaPath && (
                                            <path
                                                d={areaPath}
                                                fill="url(#detailsCurveAreaGradient)"
                                                stroke="none"
                                            />
                                        )}

                                        {points.map((point, index) => (
                                            <g key={point.label}>
                                                <rect
                                                    x={point.x - 30}
                                                    y={point.fullBarY}
                                                    width="60"
                                                    height={point.fullBarHeight}
                                                    fill="#f8c4e8"
                                                    opacity={index === 0 ? 0 : 0.9}
                                                />

                                                <rect
                                                    x={point.x - 30}
                                                    y={point.fillBarY}
                                                    width="60"
                                                    height={point.fillBarHeight}
                                                    fill="#dc2692"
                                                    opacity={
                                                        hoveredIndex === null || hoveredIndex === index
                                                            ? 1
                                                            : 0.55
                                                    }
                                                />
                                            </g>
                                        ))}

                                        <path
                                            d={smoothPath}
                                            fill="none"
                                            stroke="#dc2692"
                                            strokeWidth="2.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            vectorEffect="non-scaling-stroke"
                                        />

                                        {points.map((point, index) => (
                                            <circle
                                                key={`dot-${point.label}`}
                                                cx={point.x}
                                                cy={point.y}
                                                r={hoveredIndex === index ? 7 : 5.5}
                                                fill="#dc2692"
                                                stroke="#ffffff"
                                                strokeWidth="2.4"
                                                vectorEffect="non-scaling-stroke"
                                            />
                                        ))}

                                        {points.map((point, index) =>
                                            index === 0 ? null : (
                                                <foreignObject
                                                    key={`value-${point.label}`}
                                                    x={point.x - 25}
                                                    y={point.baseY - 24}
                                                    width="50"
                                                    height="22"
                                                >
                                                    <div className="details-chart-value">
                                                        {point.value}%
                                                    </div>
                                                </foreignObject>
                                            )
                                        )}

                                        {points.map((point) => (
                                            <foreignObject
                                                key={`label-${point.label}`}
                                                x={point.x - 48}
                                                y={point.baseY + 18}
                                                width="96"
                                                height="26"
                                            >
                                                <div className="details-chart-label">
                                                    {point.label}
                                                </div>
                                            </foreignObject>
                                        ))}
                                    </svg>

                                    {points.map((point, index) => (
                                        <div
                                            key={`hover-${point.label}`}
                                            className="details-chart-hover-area"
                                            style={{ left: `${(point.x / chart.width) * 100}%` }}
                                            onMouseEnter={() => setHoveredIndex(index)}
                                            onMouseLeave={() => setHoveredIndex(null)}
                                        />
                                    ))}

                                    {activePoint && (
                                        <div
                                            className="details-chart-tooltip"
                                            style={{
                                                left: `${(activePoint.x / chart.width) * 100}%`,
                                                top: `${(activePoint.y / chart.height) * 100}%`,
                                            }}
                                        >
                                            <strong>{activePoint.label}</strong>
                                            <div>
                                                <span />
                                                {activePoint.customers.toLocaleString()} customers{" "}
                                                <b>({activePoint.value}%)</b>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div style={insightBoxStyle}>
                                    <div style={{ fontSize: 25, marginBottom: 0 }}>
                                        <Alarm color="#E22FA1" />
                                    </div>

                                    <h3 style={insightTitleStyle}>
                                        You lose most customers between 1st to 2nd visit.
                                    </h3>

                                    <div style={insightPointStyle}>
                                        <Stars color="#E22FA1" size={22} />

                                        <p style={{ margin: 0 }}>
                                            Out of {firstVisitData.customers.toLocaleString()} customers
                                            who made 1st visit,{" "}
                                            {secondVisitData.dropOffCount.toLocaleString()} (
                                            {secondVisitData.dropOffPercent.toFixed(1)}%) didn't make
                                            the 2nd visit
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    <div className="dropoff-table-wrap">
                        <Table borderless responsive className="dropoff-table">
                            <thead>
                                <tr>
                                    <th>Visit</th>
                                    <th>Total Customer Making Visits (#)</th>
                                    <th>Customer Dropoff (%) ↕</th>
                                </tr>
                            </thead>

                            <tbody>
                                {processedData.map((item, index) => (
                                    <tr key={item.visit}>
                                        <td>{item.visit}</td>

                                        <td>
                                            {item.customers.toLocaleString()} (
                                            {item.retainedPercent.toFixed(
                                                item.retainedPercent % 1 === 0 ? 0 : 1
                                            )}
                                            %)
                                        </td>

                                        <td>
                                            {index === 0
                                                ? "-"
                                                : `${item.dropOffCount.toLocaleString()} (${item.dropOffPercent.toFixed(
                                                    1
                                                )}%)`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            <Footer />

            <style>
                {`
          .details-chart-wrap {
            height: 315px;
            position: relative;
            width: 100%;
          }

          .details-chart-svg {
            width: 100%;
            height: 100%;
            overflow: visible;
          }

          .details-y-label {
            font-size: 12px;
            fill: #344244;
            font-weight: 500;
          }

          .details-chart-value {
            height: 20px;
            color: #ffffff;
            border-radius: 3px;
            font-size: 11px;
            font-weight: 900;
            line-height: 20px;
            text-align: center;
            white-space: nowrap;
          }

          .details-chart-label {
            font-size: 12px;
            color: #52666c;
            text-align: center;
            white-space: nowrap;
          }

          .details-chart-hover-area {
            position: absolute;
            top: 0;
            width: 110px;
            height: 100%;
            transform: translateX(-50%);
            cursor: pointer;
            z-index: 10;
          }

          .details-chart-tooltip {
            position: absolute;
            transform: translate(-50%, -92%);
            background: #ffffff;
            border: 1px solid #d5d5d5;
            box-shadow: 0 5px 16px rgba(0, 0, 0, 0.22);
            border-radius: 14px;
            padding: 13px 16px;
            min-width: 220px;
            z-index: 30;
            pointer-events: none;
            color: #071c1f;
          }

          .details-chart-tooltip strong {
            display: block;
            font-size: 14px;
            margin-bottom: 8px;
            font-weight: 800;
          }

          .details-chart-tooltip div {
            font-size: 14px;
            display: flex;
            align-items: center;
            gap: 8px;
            white-space: nowrap;
          }

          .details-chart-tooltip span {
            width: 11px;
            height: 11px;
            border-radius: 50%;
            background: #dc2692;
            display: inline-block;
            flex: 0 0 auto;
          }

         .dropoff-table-wrap {
            margin-top: 42px;
        }

        .dropoff-table {
            border-collapse: separate !important;
            border-spacing: 0 16px !important;
            margin-bottom: 0;
        }

        .dropoff-table thead th {
            font-size: 17px;
            color: #344244;
            font-weight: 800;
            padding: 0 24px 6px !important;
            border: none !important;
            white-space: nowrap;
            text-align: center;
        }

        .dropoff-table tbody tr {
            background: #f6f8f8;
            transition: all 0.2s ease;
            cursor: pointer;
            text-align: center;
        }

        .dropoff-table tbody tr:hover {
            background: #edf5f5;
        }

        .dropoff-table tbody td {
            padding: 10px 24px !important;
            font-size: 16px;
            font-weight: 500;
            color: #344244;
            border: none !important;
            vertical-align: middle;
        }

        .dropoff-table tbody td:first-child {
            border-radius: 16px 0 0 16px;
            font-weight: 600;
        }

        .dropoff-table tbody td:last-child {
            border-radius: 0 16px 16px 0;
        }

        .dropoff-table tbody tr:hover td {
            color: #071c1f;
            background-color: #e3e3e3;
        }
        
          @media (max-width: 992px) {
            .dropoff-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
            </style>
        </>
    );
};

const pageWrapperStyle = {
    background: "#ffffff",
    minHeight: "100vh",
    padding: 28,
};

const topActionStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
};

const backButtonStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 14,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
};

const exportButtonStyle = {
    border: "1px solid #d8dee4",
    borderRadius: 12,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "12px 18px",
};

const pageTitleStyle = {
    fontSize: 34,
    fontWeight: 700,
    color: "#071C1F",
    marginBottom: 26,
};

const filterBarStyle = {
    background: "#F6F7F7",
    borderRadius: 14,
    padding: 14,
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 24,
};

const filterStyle = {
    minWidth: 230,
    border: "1px solid #d8dee4",
    borderRadius: 10,
    fontWeight: 600,
    color: "#17292b",
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
};

const mainCardStyle = {
    border: "1px solid #dce5e7",
    borderRadius: 18,
    boxShadow: "none",
};

const mainMetricStyle = {
    fontSize: 42,
    fontWeight: 700,
    color: "#071C1F",
    marginBottom: 12,
};

const mutedMetricStyle = {
    color: "#6B7C7F",
    fontWeight: 500,
};

const subTextStyle = {
    fontSize: 18,
    color: "#17292b",
    marginBottom: 30,
};

const chartInsightGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 360px",
    gap: 34,
    alignItems: "center",
};

const insightBoxStyle = {
    background: "#FCEFFA",
    borderRadius: 20,
    padding: "16px 28px",
    minHeight: 250,
};

const insightTitleStyle = {
    fontSize: 22,
    fontWeight: 600,
    color: "#030B12",
    lineHeight: 1.25,
    marginBottom: 28,
};

const insightPointStyle = {
    display: "grid",
    gridTemplateColumns: "24px 1fr",
    gap: 12,
    fontSize: 16,
    color: "#030B12",
    lineHeight: 1.35,
};

const tableHeadStyle = {
    fontSize: 17,
    color: "#344244",
    fontWeight: 800,
    padding: "0 22px 16px",
    whiteSpace: "nowrap",
};

const tableCellStyle = {
    fontSize: 16,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
};

export default CustomerDropOffInsightDetails;